import { useUserEventLogger } from '@repo/tracking-service';
import { LottiePlayer } from '@repo/ui';
import { useDialog } from '@repo/ui';
import { Button, TopNavigation } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { useAtomValue } from 'jotai';
import Image from 'next/image';
import { useRouter } from 'next/router';

import common from '@/components/survey/common/ui/styles/common.module.scss';
import s from '@/components/survey/start/StartSurveyContainer.module.scss';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { hasEmployeeAtom, someCompletedBusinessSurveyAtom, surveyStatusAtom } from '@/lib/stores/survey/common';

export const StartSurveyContainer = () => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const { showDialog } = useDialog();
  const surveyStatus = useAtomValue(surveyStatusAtom);
  const hasEmployee = useAtomValue(hasEmployeeAtom);
  const someCompletedBusinessSurvey = useAtomValue(someCompletedBusinessSurveyAtom);

  const { deductionTypes, expiredAt } = surveyStatus;

  const startSurvey = () => {
    if (surveyStatus.isCompleted) {
      router.push(SURVEY_PAGES.설문완료);
      return;
    }
    const now = new Date();
    if (now > new Date(surveyStatus.expiredAt)) {
      router.push(SURVEY_PAGES.만료);
      return;
    }
    /**
     * 근로자 있으면 대표특관자 질문
     * 근로자 없음 + 답변 완료한 사업장 존재
     * 근로자 없음 + 첫 설문 시작
     * */
    const nextPage = hasEmployee ? SURVEY_PAGES.대표특관자 : someCompletedBusinessSurvey ? SURVEY_PAGES.사업장별질문 : SURVEY_PAGES.intro;
    router.push(nextPage);
  };

  const expiredDate = new Date(expiredAt);
  const expiredMessage = getSubmissionMessage(expiredDate);

  if (!surveyStatus.surveyAnswerId) return <></>;
  return (
    <>
      <TopNavigation.Basic
        rightTextButton={{
          label: '나가기',
          onClick: () => {
            showDialog({
              title: '나가시면 환급 일정이 늦어져요',
              description:
                '지금 답변을 제출하시면\n환급금을 보다 빨리 받아보실 수 있어요.\n\n[나중에 하기]를 선택하시면,\n별도의 카카오톡 알림톡으로 안내드릴게요.',
              buttons: {
                secondary: {
                  text: '나중에 하기',
                  onClick: () => {
                    sendClickEvent('survey_start_dialog-later');
                    router.push(COMMON_PATHS.환급조회목록);
                  }
                },
                primary: {
                  text: '답변 시작',
                  onClick: () => {
                    sendClickEvent('survey_start_dialog-start');
                    startSurvey();
                  }
                }
              }
            });
          }
        }}
      />
      <main className={cn(common.main_layout, 'pt-[47px]', 'pb-[143px]')}>
        <section className={'flex flex-col items-center px-[28px] py-5'}>
          <div className={'-mt-[28px] h-[137px] w-[137px]'}>
            <LottiePlayer src={'/lotties/ani_coin-pencil.json'} loop={false} />
          </div>
          <h1 className={'headline-20 -mt-[30px] py-4 text-center text-gray-900'}>
            더 빠르고 정확한 환급을 위해
            <br />
            사업장에 대해 답변이 필요해요!
          </h1>
        </section>
        <section>
          <ul className={s.gray_rounded_box}>
            {/* TODO: 양도세 추가로 임시 제거. 추후 서버에서 양도세 값 받아서 종합소득세 + 양도세 금액으로 노출 검토 */}
            {/*<li className={s.list_item}>*/}
            {/*  <Image src={'/images/illus-icons/fill-moneybag-gn-rg.svg'} alt={'환급액'} width={32} height={32} className={s.list_icon} />*/}
            {/*  <div className={s.list_textbox}>*/}
            {/*    <p className={s.list_overtext}>{taxpayerName}님의 예상 환급액</p>*/}
            {/*    <p className={s.list_title}>{numberWithCommas(totalAmount)}원</p>*/}
            {/*  </div>*/}
            {/*</li>*/}
            <li className={s.list_item}>
              <Image src={'/images/illus-icons/fill-search-rg.svg'} alt={'감면'} width={32} height={32} className={s.list_icon} />
              <div className={s.list_textbox}>
                <p className={s.list_overtext}>받지 못했던 감면 혜택</p>
                <p className={s.list_title}>{deductionTypes.length}개 발견됐어요</p>
              </div>
            </li>
            <li className={s.list_item}>
              <Image src={'/images/illus-icons/fill-5-clock-rg.svg'} alt={'시간'} width={32} height={32} className={s.list_icon} />
              <div className={s.list_textbox}>
                <p className={s.list_overtext}>빠르게 답변한 분들의 환급 일정</p>
                <p className={s.list_title}>평균 7일 빨라졌어요</p>
              </div>
            </li>
          </ul>
        </section>

        <Button.StickyBottom.Wrapper className={'px-4.5'}>
          <div className={'flex w-full flex-col gap-3'}>
            <p className={'body-16 text-center text-gray-600'}>{expiredMessage}</p>
            <Button.StickyBottom.Button
              type={'button'}
              state={'primary'}
              onClick={() => {
                sendClickEvent('survey_start_cta');
                startSurvey();
              }}
            >
              답변 시작하기
            </Button.StickyBottom.Button>
          </div>
        </Button.StickyBottom.Wrapper>
      </main>
    </>
  );
};

function getSubmissionMessage(dueDate: Date): string {
  const currentTime = new Date();

  // 현재 시각과 만료일까지 남은 일수 계산
  const oneDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.ceil((dueDate.getTime() - currentTime.getTime()) / oneDay);

  if (daysLeft === 3) {
    return '답변 제출까지 3일 남았어요.';
  } else if (daysLeft === 2) {
    return '답변 제출까지 2일 남았어요.';
  } else if (daysLeft === 1) {
    return '오늘까지 꼭 제출해주세요.';
  } else if (daysLeft > 3) {
    return `답변 제출까지 ${daysLeft}일 남았어요.`;
  }
  return '기한이 만료되었습니다.';
}
